/**
  * @fileoverview Step 8 of the department onboarding wizard: authentication settings and initial admin assignment.
  * @module system-web/pages/tenants/wizard/steps/AdminAuthStep
  *
  * Responsibilities:
  * - Capture tenant authentication configuration.
  * - Capture the initial admin identity and whether the invite email should be sent immediately.
  *
  * Dependencies:
  * - WizardState
  * - ValidatedEmailInput
  * - listSystemUsers
  *
  * Architecture:
  * - Local-first UI step. All edits go back into the wizard draft state; only the final provisioning call is online.
  *
  * Notes:
  * - Deferring the invite still designates the admin for provisioning purposes; it only postpones email dispatch.
  */

import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import type { WizardState } from '../types';
import type { StepError } from '../validateStep';
import ValidatedEmailInput from '../../../../components/ValidatedEmailInput';
import { listSystemUsers } from '../../../../services/systemUserService';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  departmentMemberships?: Array<{ departmentId: string; roles: string[] }>;
  organizationMemberships?: Array<{ organizationId: string; roles: string[] }>;
}

interface Props {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  errors?: StepError[];
}

const AUTH_METHODS = [
  { code: 'local', label: 'Local Accounts', desc: 'Email + password via Entra External ID', tier: 'Tier 1+' },
  { code: 'microsoft365', label: 'Microsoft 365 / Entra ID', desc: 'M365 work/school accounts via OIDC', tier: 'Tier 2+ (add-on)' },
  { code: 'google', label: 'Google Workspace', desc: 'Google Workspace domain accounts', tier: 'Tier 2+ (add-on)' },
  { code: 'saml', label: 'Enterprise SAML', desc: 'SAML 2.0 federation (Okta, Ping, etc.)', tier: 'Tier 2+ (add-on)' },
];

export default function AdminAuthStep({ state, onChange, errors = [] }: Props) {
  const hasError = (field: string) => errors.some((e) => e.field === field);
  const errCls = (field: string) => hasError(field) ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-300';
  const auth = state.authenticationSettings;
  const admin = state.initialAdmin;
  const isLocal = auth.authMethod === 'local';
  const isFederated = auth.authMethod && auth.authMethod !== 'local';
  const contacts = state.contacts || [];
  const selectionMode = admin.selectionMode || 'manual';
  const sendInviteImmediately = admin.sendInviteImmediately !== false;

  const [systemUsers, setSystemUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const updateAuth = (patch: Record<string, any>) => onChange({ authenticationSettings: { ...auth, ...patch } });
  const updateSession = (patch: Record<string, any>) => onChange({ authenticationSettings: { ...auth, sessionPolicy: { ...auth.sessionPolicy, ...patch } } });
  const updateAdmin = (patch: Record<string, any>) => onChange({ initialAdmin: { ...admin, ...patch } });
  const updateM365 = (patch: Record<string, any>) => onChange({ authenticationSettings: { ...auth, microsoft365: { ...auth.microsoft365, ...patch } } });
  const updateGoogle = (patch: Record<string, any>) => onChange({ authenticationSettings: { ...auth, google: { ...auth.google, ...patch } } });
  const updateSaml = (patch: Record<string, any>) => onChange({ authenticationSettings: { ...auth, saml: { ...auth.saml, ...patch } } });

  // Fetch existing system users when selection mode changes to existing_user
  useEffect(() => {
    if (selectionMode === 'existing_user' && systemUsers.length === 0) {
      setLoadingUsers(true);
      listSystemUsers()
        .then((users) => setSystemUsers(users.filter((u: any) => u.status === 'active') as User[]))
        .catch((err: any) => console.error('Failed to load system users:', err))
        .finally(() => setLoadingUsers(false));
    }
  }, [selectionMode, systemUsers.length]);

  // Handle selection mode change
  const handleSelectionModeChange = (mode: 'existing_user' | 'contact' | 'manual') => {
    updateAdmin({
      selectionMode: mode,
      existingUserId: undefined,
      contactId: undefined,
      firstName: '',
      lastName: '',
      email: '',
    });
  };

  // Handle existing user selection
  const handleExistingUserSelect = (userId: string) => {
    const user = systemUsers.find(u => u._id === userId);
    if (user) {
      updateAdmin({
        existingUserId: userId,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      });
    }
  };

  // Handle contact selection
  const handleContactSelect = (contactId: string) => {
    const contact = contacts.find(c => c._id === contactId);
    if (contact) {
      const primaryEmail = contact.emails.find(e => e.isPrimary) || contact.emails[0];
      const nameParts = contact.name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      updateAdmin({
        contactId,
        firstName,
        lastName,
        email: primaryEmail?.address || '',
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* Auth Method */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Authentication Method</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {AUTH_METHODS.map((method) => {
            const disabled = method.code !== 'local' && state.tier === 'professional';
            return (
              <button
                key={method.code}
                type="button"
                disabled={disabled}
                onClick={() => updateAuth({ authMethod: method.code })}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  auth.authMethod === method.code
                    ? 'border-orange-500 bg-orange-50'
                    : disabled
                      ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">{method.label}</span>
                  <span className="text-xs text-gray-400">{method.tier}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{method.desc}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* MFA (local only) */}
      {isLocal && (
        <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4" /> Multi-Factor Authentication
          </h3>
          <label className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              checked={auth.mfa?.required ?? false}
              onChange={(e) => updateAuth({ mfa: { ...auth.mfa, required: e.target.checked, methods: auth.mfa?.methods || ['authenticatorApp'] } })}
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            <span className="text-sm text-blue-800">Require MFA for all users</span>
          </label>
          {auth.mfa?.required && (
            <div className="flex gap-4 ml-6">
              {(['totp', 'email', 'sms', 'fido2'] as const).map((method) => (
                <label key={method} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={auth.mfa?.methods?.includes(method) ?? false}
                    onChange={(e) => {
                      const methods = auth.mfa?.methods || [];
                      updateAuth({ mfa: { ...auth.mfa, required: true, methods: e.target.checked ? [...methods, method] : methods.filter((m) => m !== method) } });
                    }}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-xs text-blue-700 capitalize">{method === 'totp' ? 'Authenticator App (TOTP)' : method === 'fido2' ? 'Hardware Key (FIDO2)' : method.toUpperCase()}</span>
                </label>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Microsoft 365 Config */}
      {auth.authMethod === 'microsoft365' && (
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Microsoft 365 / Entra ID Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Entra Tenant ID</label>
              <input type="text" value={auth.microsoft365?.tenantId || ''} onChange={(e) => updateM365({ tenantId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Primary Domain</label>
              <input type="text" value={auth.microsoft365?.domain || ''} onChange={(e) => updateM365({ domain: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="department.gov" />
            </div>
          </div>
        </section>
      )}

      {/* Google Workspace Config */}
      {auth.authMethod === 'google' && (
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Google Workspace Configuration</h3>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Workspace Domain</label>
            <input type="text" value={auth.google?.workspaceDomain || ''} onChange={(e) => updateGoogle({ workspaceDomain: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="department.gov" />
          </div>
        </section>
      )}

      {/* SAML Config */}
      {auth.authMethod === 'saml' && (
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">SAML 2.0 Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">IdP Entity ID</label>
              <input type="text" value={auth.saml?.idpEntityId || ''} onChange={(e) => updateSaml({ idpEntityId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://idp.example.com/entity" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">IdP SSO URL</label>
              <input type="url" value={auth.saml?.idpSsoUrl || ''} onChange={(e) => updateSaml({ idpSsoUrl: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Metadata URL (optional)</label>
              <input type="url" value={auth.saml?.metadataUrl || ''} onChange={(e) => updateSaml({ metadataUrl: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
        </section>
      )}

      {/* Session Policy */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Session Policy</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Admin Idle Timeout (minutes)</label>
            <input type="number" min="5" max="480" value={auth.sessionPolicy?.adminIdleTimeoutMinutes ?? 60} onChange={(e) => updateSession({ adminIdleTimeoutMinutes: parseInt(e.target.value) || 60 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Token Refresh (hours)</label>
            <input type="number" min="1" max="168" value={auth.sessionPolicy?.mobileTokenRefreshHours ?? 24} onChange={(e) => updateSession({ mobileTokenRefreshHours: parseInt(e.target.value) || 24 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>
      </section>

      {/* Initial Admin */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Initial Admin User</h3>
        <p className="text-xs text-gray-500 mb-3">
          {selectionMode === 'existing_user' 
            ? 'Assign an existing system user as the admin for this department.'
            : sendInviteImmediately
              ? 'This person will receive an invitation email during provisioning so they can set up their account right away.'
              : 'This person will be designated as the initial department admin, but the invitation email will be held for a later send.'}
        </p>

        {/* Selection Mode Radio Buttons */}
        <div className="mb-4 space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="adminSelectionMode"
              checked={selectionMode === 'existing_user'}
              onChange={() => handleSelectionModeChange('existing_user')}
              className="h-4 w-4 text-orange-600 border-gray-300 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-700">Select existing system user</span>
            <span className="text-xs text-gray-500">(any user already in the system)</span>
          </label>
          {contacts.length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="adminSelectionMode"
                checked={selectionMode === 'contact'}
                onChange={() => handleSelectionModeChange('contact')}
                className="h-4 w-4 text-orange-600 border-gray-300 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-700">Select from contacts</span>
              <span className="text-xs text-gray-500">(contacts entered in Step 3)</span>
            </label>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="adminSelectionMode"
              checked={selectionMode === 'manual'}
              onChange={() => handleSelectionModeChange('manual')}
              className="h-4 w-4 text-orange-600 border-gray-300 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-700">Enter manually</span>
            <span className="text-xs text-gray-500">(create new user invitation)</span>
          </label>
        </div>

        {/* Existing User Dropdown */}
        {selectionMode === 'existing_user' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select User</label>
            {loadingUsers ? (
              <div className="text-sm text-gray-500 py-2">Loading users...</div>
            ) : (
              <select
                value={admin.existingUserId || ''}
                onChange={(e) => handleExistingUserSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">-- Select a user --</option>
                {systemUsers.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.firstName} {u.lastName} ({u.email})
                    {u.departmentMemberships && u.departmentMemberships.length > 0 && ` - ${u.departmentMemberships.length} dept(s)`}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Contact Dropdown */}
        {selectionMode === 'contact' && contacts.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Contact</label>
            <select
              value={admin.contactId || ''}
              onChange={(e) => handleContactSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">-- Select a contact --</option>
              {contacts.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name} {c.isPrimary && '(Primary)'}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Manual Entry or Display Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">First Name *</label>
            <input
              type="text"
              value={admin.firstName || ''}
              onChange={(e) => updateAdmin({ firstName: e.target.value })}
              disabled={selectionMode === 'existing_user' && !!admin.existingUserId}
              className={`w-full px-3 py-2 border ${errCls('initialAdmin.firstName')} rounded-lg text-sm ${selectionMode === 'existing_user' && admin.existingUserId ? 'bg-gray-50' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Last Name *</label>
            <input
              type="text"
              value={admin.lastName || ''}
              onChange={(e) => updateAdmin({ lastName: e.target.value })}
              disabled={selectionMode === 'existing_user' && !!admin.existingUserId}
              className={`w-full px-3 py-2 border ${errCls('initialAdmin.lastName')} rounded-lg text-sm ${selectionMode === 'existing_user' && admin.existingUserId ? 'bg-gray-50' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
            <ValidatedEmailInput
              value={admin.email || ''}
              onChange={(v) => updateAdmin({ email: v })}
              disabled={selectionMode === 'existing_user' && !!admin.existingUserId}
              className={`w-full px-3 py-2 border ${errCls('initialAdmin.email')} rounded-lg text-sm pr-9 ${selectionMode === 'existing_user' && admin.existingUserId ? 'bg-gray-50' : ''}`}
              placeholder="admin@department.gov"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <input type="text" value="Department Admin" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500" readOnly />
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={sendInviteImmediately}
              onChange={(e) => updateAdmin({ sendInviteImmediately: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span>
              <span className="block text-sm font-medium text-amber-900">Send initial admin invite immediately</span>
              <span className="block text-xs text-amber-800 mt-1">
                {sendInviteImmediately
                  ? 'Provisioning will send the initial admin email as soon as the department background setup reaches the invite step.'
                  : 'Provisioning will create the admin user and department-admin membership, but the email invite will remain pending until you send it later.'}
              </span>
            </span>
          </label>
        </div>
      </section>
    </div>
  );
}
